/**
 * LeichenZeile — eine Treffer-Zeile innerhalb einer KategorieGruppe (Plan 02-05 Task 2).
 * Vorbild: das Zeilen-Layout des Preis-Warn-Vorbildprogramms (Badges, Einzel-Aktion),
 * deutlich simpler: keine EK-/Preis-Aufklapp-Details, dafuer Leichen-Typ-Badges, echtes
 * Anlagedatum/letzter Verkauf (LIST-01) und der Aktiv-Status-Badge (LIST-06).
 *
 * Identitaet ist `itemId` (Artikel-Ebene) — kein zusammengesetzter Key.
 */
import { Snowflake, RotateCcw, ShieldCheck } from 'lucide-react';
import { Button } from '@libero/core/components/ui/button';
import type { KandidatRow, LeichenTyp } from '../types';
import { leichenTypen } from '../types';
import { datumFmt } from '../lib/format';

interface LeichenZeileProps {
  row: KandidatRow;
  selected: boolean;
  onToggleSelect: () => void;
  onSnoozeEinzeln: (itemId: number) => void;
  onAufheben: (itemId: number) => void;
  onWhitelistEinzeln: (itemId: number) => void;
}

const LEICHEN_TYP_LABEL: Record<LeichenTyp, string> = {
  keinMandant: 'Kein Mandant',
  keinBestand: 'Kein Bestand ≥90 T',
  keinVerkauf: 'Kein Verkauf ≥365 T',
};

const LEICHEN_TYP_CLASS: Record<LeichenTyp, string> = {
  keinMandant: 'bg-orange-50 text-orange-800 border border-orange-200',
  keinBestand: 'bg-blue-50 text-blue-800 border border-blue-200',
  keinVerkauf: 'bg-purple-50 text-purple-800 border border-purple-200',
};

const STATUS_LABEL: Record<KandidatRow['anzeigeStatus'], string> = {
  neu: 'Neu',
  offen: 'Offen',
  gesnoozed: 'Gesnoozed',
  behoben: 'Behoben',
};

const STATUS_CLASS: Record<KandidatRow['anzeigeStatus'], string> = {
  neu: 'bg-[#E9FF57] text-[#1A1A1A] border border-[#1A1A1A]',
  offen: 'bg-white text-[#1A1A1A] border border-[#DAD9D9]',
  gesnoozed: 'bg-[#DAD9D9] text-[#1A1A1A] border border-[#DAD9D9]',
  behoben: 'bg-green-50 text-green-800 border border-green-200',
};

export default function LeichenZeile({
  row,
  selected,
  onToggleSelect,
  onSnoozeEinzeln,
  onAufheben,
  onWhitelistEinzeln,
}: LeichenZeileProps) {
  const typen = leichenTypen(row);
  const lookupKey = row.artikelNummer ?? String(row.itemId);
  const letzterVerkaufAnzeige = row.letzterVerkaufAm === null ? '> 365 T' : datumFmt(row.letzterVerkaufAm);

  return (
    <div className="border-t border-[#DAD9D9] first:border-t-0">
      <div className="flex items-center gap-3 py-2 px-3 flex-wrap">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Zeile ${lookupKey} auswaehlen`}
          className="h-4 w-4"
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-gray-500">{row.artikelNummer ?? '—'}</span>
            <span className="text-sm text-[#1A1A1A] font-semibold truncate">{row.artikelName ?? '—'}</span>
          </div>
          <div className="text-xs text-gray-500">{row.kategorieName ?? 'Ohne Kategorie'}</div>
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          {typen.map((typ) => (
            <span key={typ} className={`rounded px-2 py-0.5 text-xs font-bold whitespace-nowrap ${LEICHEN_TYP_CLASS[typ]}`}>
              {LEICHEN_TYP_LABEL[typ]}
            </span>
          ))}
        </div>

        <div className="text-xs text-[#1A1A1A] whitespace-nowrap">
          <div className="text-[10px] font-bold uppercase text-gray-500">Anlagedatum</div>
          {datumFmt(row.plentyCreatedAt)}
        </div>
        <div className="text-xs text-[#1A1A1A] whitespace-nowrap">
          <div className="text-[10px] font-bold uppercase text-gray-500">Letzter Verkauf</div>
          {letzterVerkaufAnzeige}
        </div>

        <span
          className={`rounded px-2 py-0.5 text-xs font-bold whitespace-nowrap ${
            row.istAktiv ? 'bg-white text-[#1A1A1A] border border-[#DAD9D9]' : 'bg-[#DAD9D9] text-[#1A1A1A] border border-[#DAD9D9]'
          }`}
        >
          {row.istAktiv ? 'Aktiv' : 'Inaktiv'}
        </span>

        <span className={`rounded px-2 py-0.5 text-xs font-bold whitespace-nowrap ${STATUS_CLASS[row.anzeigeStatus]}`}>
          {STATUS_LABEL[row.anzeigeStatus]}
        </span>

        <div className="flex items-center gap-2 ml-auto">
          {row.anzeigeStatus === 'gesnoozed' ? (
            <Button type="button" size="sm" variant="outline" onClick={() => onAufheben(row.itemId)}>
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Aufheben
            </Button>
          ) : (
            <Button type="button" size="sm" variant="outline" onClick={() => onSnoozeEinzeln(row.itemId)}>
              <Snowflake className="w-3.5 h-3.5 mr-1" /> Snoozen
            </Button>
          )}
          <Button type="button" size="sm" variant="outline" onClick={() => onWhitelistEinzeln(row.itemId)}>
            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Whitelisten
          </Button>
        </div>
      </div>
    </div>
  );
}
